/* eslint-disable no-constant-condition */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type FormikProps } from 'formik';
import Loading from '@components/Shared/Loader/Loading';
import PopoverCustom from '@components/Shared/Popover/PopoverCustom';
import type { MaterialTransactionsLineList } from '@store/INV/Transactions/SharedModels';
import NeatixModal from '@components/Modal/NeatixModal';
import { Card, CardBody } from '@components/card/card';
import AddLotItem from '../../INVSettings/MasterItems/MasterItemsDetails/LotItems/AddLotItem';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { fetchDataByReferenceId, cleanUpItemsList } from '@store/INV/Transactions/InvIssue/invSlice';
import useGetSubinventoryList from '@hooks/INV/Inventories/useGetSubinventoryList';
import { TreeSelect } from 'antd';

// ✅ Picker
import { useItemUomPicker, ItemUomPickerModal } from '@pages/INV/lookups/useItemUomPicker';

// ✅ API (lookup) - same as IssueLines
import {
  lookupItemsWithUomRowsByItemIds,
  mergeIntoDetailsMap,
  makeDetailsKey
} from '@pages/INV/lookups/itemsWithUomLookup.logic';

interface LotItem {
  id: string;
  lotNumber: string;
  expirationDate?: string;
  itemId?: string;
}

interface LinesProps {
  formik: FormikProps<any>;
  isUpdate?: boolean;
}

type ItemType = 'InventoryItem';
const DEFAULT_ITEM_TYPE: ItemType = 'InventoryItem';

const n = (v: any) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

const PAGE_SIZE = 20;

type LotRow = {
  lotId: any; // null => None
  lotNumber?: string | null;
  expirationDate?: string | null;
};

const ReceiveLines: React.FC<LinesProps> = ({ formik, isUpdate = false }) => {
  const { TreeSubInventoryData } = useGetSubinventoryList();
  const { values, setFieldValue } = formik;
  const { i18n, t } = useTranslation();

  const dispatch = useAppDispatch();
  const { itemsList, loading: loading1, error: errorfetchItems } = useAppSelector(state => state.Inv);

  const isPosted = values.postedFlag === true;
  // const showUnitPrice = values.methodTransfer === 2; // لإظهار عمود السهر او لا .. في  امر تحويل
  const showUnitPrice = values.code === 'MOR'; // لإظهار عمود السهر او لا .. في  امر تحويل

  // console.log(values);

  // ✅ Manual Receive codes
  const MANUAL_RECEIVE_CODES = ['MIR'];
  const isManualReceive = MANUAL_RECEIVE_CODES.includes(values.code);
  const isReferenceReceive = !isManualReceive;

  const readOnlyByCode = isReferenceReceive;

  const canPickItem = !isPosted && !readOnlyByCode;
  const canChangeUom = !isPosted && !readOnlyByCode;
  const canAddLine = !isPosted && !readOnlyByCode;
  const canDeleteLine = !isPosted && !readOnlyByCode;
  const canEditQty = !isPosted;

  // ✅ Picker
  const { pickItemUom, modalProps } = useItemUomPicker();

  // ✅ detailsMap (same pattern as IssueLines)
  const [detailsMap, setDetailsMap] = useState<Map<string, any>>(new Map());
  const detailsMapRef = useRef(detailsMap);
  useEffect(() => {
    detailsMapRef.current = detailsMap;
  }, [detailsMap]);

  // ✅ fast access by itemId (avoid staying as ID)
  const anyRowByItemId = useMemo(() => {
    const m = new Map<string, any>();
    for (const r of detailsMap.values()) {
      if (!r?.itemId) continue;
      const k = String(r.itemId);
      if (!m.has(k)) m.set(k, r);
    }
    return m;
  }, [detailsMap]);
  const anyRowByItemIdRef = useRef(anyRowByItemId);
  useEffect(() => {
    anyRowByItemIdRef.current = anyRowByItemId;
  }, [anyRowByItemId]);

  const [detailsLoading, setDetailsLoading] = useState(false);
  const detailsAbortRef = useRef<AbortController | null>(null);

  // ✅ smooth debounce + request sequencing
  const detailsTimerRef = useRef<any>(null);
  const detailsReqSeqRef = useRef(0);
  const prevVisibleCountRef = useRef(PAGE_SIZE);

  // ✅ "loading on button" only
  const [loadingMore, setLoadingMore] = useState(false);

  // ✅ UOM preload
  const loadedUomsRef = useRef(new Set<string>());
  const uomsAbortRef = useRef<AbortController | null>(null);
  const [uomsLoading, setUomsLoading] = useState(false);

  // ✅ Lot add modal (AddLotItem)
  const [showAddLotModal, setShowAddLotModal] = useState(false);
  const [lineData, setLineData] = useState<any>();
  const [addedLots, setAddedLots] = useState<Record<string, LotItem | null>>({});

  // ✅ Other inventory alert
  const [showOtherInventoryAlert, setShowOtherInventoryAlert] = useState(false);
  const [otherInventoryItemsCount, setOtherInventoryItemsCount] = useState(0);

  // =========================
  // ✅ Pagination (NO reverse)
  // =========================
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setLoadingMore(false);
    prevVisibleCountRef.current = PAGE_SIZE;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.inventoryId, values.subInventoryId, values.referenceId, values.code]);

  const allLines: any[] = values.materialTransactionsLineList || [];

  const visibleLines: any[] = useMemo(() => {
    return allLines.slice(0, visibleCount);
  }, [allLines, visibleCount]);

  const hasMoreLines = allLines.length > visibleCount;
  const remainingToShow = Math.max(0, Math.min(PAGE_SIZE, allLines.length - visibleCount));

  const showMore = () => {
    setVisibleCount(v => Math.min(v + PAGE_SIZE, allLines.length));
  };

  // =========================
  // Helpers
  // =========================
  const resetLine = (line: any) => ({
    ...line,
    transactionItemId: null,
    transactionUom: '',
    lotId: null,
    transactionQuantityIn: 0,
    transactionQuantityOut: 0,
    unitPrice: 0,
    total: 0
  });

  const getInfoExact = (line: any) => {
    if (!line?.transactionItemId || !line?.transactionUom) return null;
    return detailsMap.get(makeDetailsKey(line.transactionItemId, line.transactionUom)) || null;
  };

  const getInfoAnyByItemId = (itemId: any) => {
    if (!itemId) return null;
    return anyRowByItemIdRef.current.get(String(itemId)) || null;
  };

  const getInfoLoose = (line: any) => {
    const exact = getInfoExact(line);
    if (exact) return exact;
    return getInfoAnyByItemId(line?.transactionItemId);
  };

  const getItemNameForLine = (line: any) => {
    const info = getInfoLoose(line);
    const isEn = i18n.language === 'en';
    const name = (isEn ? info?.name || info?.name2 : info?.name2 || info?.name) || '';
    if (name) return name;

    // fallback from line if exists
    return (
      (isEn
        ? line?.itemName || line?.transactionItemName || line?.itemName2 || line?.transactionItemName2
        : line?.itemName2 || line?.transactionItemName2 || line?.itemName || line?.transactionItemName) || ''
    );
  };

  const getUomNameForLine = (line: any) => {
    const info = getInfoLoose(line);
    const isEn = i18n.language === 'en';
    return (
      (isEn
        ? info?.unitOfMeasureName || info?.unitOfMeasureName2
        : info?.unitOfMeasureName2 || info?.unitOfMeasureName) || ''
    );
  };

  const getCodeOrBarcodeForLine = (line: any) => {
    const info = getInfoLoose(line);
    return info?.barcode || info?.itemCode || line?.itemCode || line?.barcode || '-';
  };

  const lineHasDuplicateItemUom = (itemId: any, uom: any, excludeIndex: number) => {
    if (!itemId || !uom) return false;
    return (values.materialTransactionsLineList || []).some((l: any, i: number) => {
      if (i === excludeIndex) return false;
      return String(l?.transactionItemId || '') === String(itemId) && String(l?.transactionUom || '') === String(uom);
    });
  };

  const getMaxAllowedQty = (line: any) => {
    // ✅ Receive: manual => ∞, reference => RemainingQty (إن وجدت)
    if (isManualReceive) return Number.POSITIVE_INFINITY;
    const remaining = line?.RemainingQty != null ? Math.max(0, n(line.RemainingQty)) : 0;
    return remaining;
  };

  const clampQty = (line: any) => {
    const max = getMaxAllowedQty(line);
    if (!Number.isFinite(max)) return line;
    const q = n(line.transactionQuantityIn);
    if (q <= max) return line;
    return { ...line, transactionQuantityIn: max };
  };

  // =========================
  // ✅ UOM options per itemId (from detailsMap only)
  // =========================
  const uomsByItemId = useMemo(() => {
    const map = new Map<string, any[]>();

    for (const r of detailsMap.values()) {
      if (!r?.itemId) continue;
      const list = map.get(String(r.itemId)) ?? [];
      list.push(r);
      map.set(String(r.itemId), list);
    }

    for (const [itemId, list] of map.entries()) {
      const uniq = new Map<string, any>();
      for (const r of list) uniq.set(String(r.unitOfMeasure), r);

      const arr = Array.from(uniq.values());
      arr.sort((a, b) => Number(!!b.isPrimaryUOM) - Number(!!a.isPrimaryUOM));
      map.set(itemId, arr);
    }

    return map;
  }, [detailsMap]);

  // =========================
  // ✅ fetch UOMs for item (include qtyByLot)
  // =========================
  const ensureItemUomsLoaded = async (itemId: string) => {
    if (!values.inventoryId || !itemId) return;

    const key = `${values.inventoryId}|${values.subInventoryId || ''}|${DEFAULT_ITEM_TYPE}|${itemId}`;
    if (loadedUomsRef.current.has(key)) return;
    loadedUomsRef.current.add(key);

    try {
      uomsAbortRef.current?.abort();
      uomsAbortRef.current = new AbortController();
      setUomsLoading(true);

      const params: any = { InventoryId: values.inventoryId, Scope: 1 };
      if (values.subInventoryId) params.SubInventoryId = values.subInventoryId;

      const rows = await lookupItemsWithUomRowsByItemIds({
        params,
        itemType: DEFAULT_ITEM_TYPE,
        itemIds: [itemId],
        pageSize: 3000,
        pageIndex: 1,
        chunkSize: 200,
        bodyExtra: {
          dynamicFields: ['qtyByLot']
        },
        opts: {
          signal: uomsAbortRef.current?.signal,
          cache: true,
          dedupeInFlight: true
        }
      });

      if (!rows.length) return;

      setDetailsMap(prev => mergeIntoDetailsMap(prev, rows));
    } catch (e: any) {
      loadedUomsRef.current.delete(key);
      if (e?.name !== 'CanceledError') console.error(e);
    } finally {
      setUomsLoading(false);
    }
  };

  // =========================
  // ✅ preload details for VISIBLE lines only
  // ✅ + debounce
  // ✅ + Abort on rapid show more
  // ✅ + loading only on button
  // =========================
  const visibleLinesKey = useMemo(() => {
    return visibleLines.map((l: any) => `${l?.transactionItemId || ''}:${l?.transactionUom || ''}`).join('|');
  }, [visibleLines]);

  useEffect(() => {
    const isLoadMore = visibleCount > prevVisibleCountRef.current;
    prevVisibleCountRef.current = visibleCount;

    if (isLoadMore) setLoadingMore(true);

    if (detailsTimerRef.current) clearTimeout(detailsTimerRef.current);

    detailsAbortRef.current?.abort();
    detailsAbortRef.current = null;

    detailsTimerRef.current = setTimeout(async () => {
      if (!values.inventoryId) {
        if (isLoadMore) setLoadingMore(false);
        return;
      }

      const lines = visibleLines || [];
      const valid = lines.filter((l: any) => l?.transactionItemId && l?.transactionUom);
      if (valid.length === 0) {
        if (isLoadMore) setLoadingMore(false);
        return;
      }

      const mapNow = detailsMapRef.current;
      const missingItemIds = new Set<string>();

      for (const l of valid) {
        const k = makeDetailsKey(l.transactionItemId, l.transactionUom);
        if (!mapNow.has(k)) missingItemIds.add(String(l.transactionItemId));
      }

      if (missingItemIds.size === 0) {
        if (isLoadMore) setLoadingMore(false);
        return;
      }

      const seq = ++detailsReqSeqRef.current;
      const thisIsLoadMore = isLoadMore;

      detailsAbortRef.current?.abort();
      detailsAbortRef.current = new AbortController();

      setDetailsLoading(true);

      try {
        const params: any = { InventoryId: values.inventoryId, Scope: 1 };
        if (values.subInventoryId) params.SubInventoryId = values.subInventoryId;

        const ids = Array.from(missingItemIds);

        const rows = await lookupItemsWithUomRowsByItemIds({
          params,
          itemType: DEFAULT_ITEM_TYPE,
          itemIds: ids,
          pageSize: 3000,
          pageIndex: 1,
          chunkSize: 200,
          bodyExtra: {
            dynamicFields: ['qtyByLot']
          },
          opts: {
            signal: detailsAbortRef.current?.signal,
            cache: true,
            dedupeInFlight: true
          }
        });

        setDetailsMap(prev => mergeIntoDetailsMap(prev, rows));
      } catch (e: any) {
        if (e?.name !== 'CanceledError') console.error(e);
      } finally {
        if (seq === detailsReqSeqRef.current) {
          setDetailsLoading(false);
          if (thisIsLoadMore) setLoadingMore(false);
        }
      }
    }, 150);

    return () => {
      if (detailsTimerRef.current) clearTimeout(detailsTimerRef.current);
      detailsAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.inventoryId, values.subInventoryId, visibleLinesKey, visibleCount]);

  // ✅ clear caches when inventory/subInventory changes
  useEffect(() => {
    setDetailsMap(new Map());
    loadedUomsRef.current.clear();
  }, [values.inventoryId, values.subInventoryId]);

  // =========================
  // Reference fetch (كما هو)
  // =========================
  const updateFieldsBasedOnCode = (data: any) => {
    setFieldValue('referenceCode', data?.data?.code ?? data?.data?.orderCode);

    // console.log(data.data);

    switch (values.code) {
      case 'INV': //
        setFieldValue('customerId', data?.data?.peopleId ?? null);
        setFieldValue('inventoryId', data?.data?.inventoryId);
        break;
      case 'SOI': //
        setFieldValue('customerId', data?.data?.peopleId ?? null);
        setFieldValue('supplierId', null);
        break;
      case 'IOI': //
        setFieldValue('subInventoryId', data?.data?.subInventoryId);
        setFieldValue('inventoryId', data?.data?.inventoryId);
        break;
      case 'SPR': //
        setFieldValue('supplierId', data?.data?.peopleId ?? null);
        setFieldValue('customerId', null);
        break;
      case 'POR': // أمـر شـراء
      // debugger
        setFieldValue('supplierId', data?.data?.peopleId ?? null);
        setFieldValue('supplierInvoiceNum', data?.data?.supplierInvoiceNum ?? values.supplierInvoiceNum ??'');
        setFieldValue('customerId', null);
        break;
      case 'BIL': //
        setFieldValue('supplierId', data?.data?.peopleId);
        setFieldValue('customerId', null);
        break;
      case 'CRP': // مردودات مبيعات نقاط البيع
      case 'CRT': // مردودات مبيعات
        setFieldValue('supplierId', null);
        setFieldValue('customerId', data?.data?.peopleId);
        break;
      case 'IOR': //
        setFieldValue('branchId', data?.data?.branchId);
        break;
      case 'MOR': // استلام أمر تحويــل
        setFieldValue('supplierId', null);
        setFieldValue('customerId', null);
        setFieldValue('inventoryId', data?.data?.inventoryTo);
        setFieldValue('methodTransfer', data?.data?.methodTransfer);
        setFieldValue('inventoryFrom', data?.data?.inventoryId);

        break;

      case 'MIR': // إضــافات متنوعـة
      case 'RFG': // استلام منتج نهائي
        setFieldValue('supplierId', null);
        setFieldValue('customerId', null);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (['MII', 'MIR'].includes(values.code)) return;

    if (values.referenceId) {
      dispatch(fetchDataByReferenceId({ referenceId: values.referenceId, code: values.code }))
        .unwrap()
        .then(({ data }) => updateFieldsBasedOnCode(data))
        .catch(err => console.error('Failed to fetch data:', err));
    }

    return () => {
      dispatch(cleanUpItemsList());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.referenceId, values.code]);

  // =========================
  // ✅ Update mode stamp + stable RemainingQty calc (مثل IssueLines)
  // =========================
  useEffect(() => {
    if (!isUpdate) return;

    const lines = values.materialTransactionsLineList || [];
    if (!lines.length) return;

    const needStamp = lines.some((l: any) => l.__origQty == null);
    if (!needStamp) return;

    const patched = lines.map((l: any) => ({
      ...l,
      __origQty: l.__origQty ?? n(l.transactionQuantityIn)
    }));

    formik.setFieldValue('materialTransactionsLineList', patched, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdate]);

  // =========================
  // itemsList -> lines (مع alert + تحديث RemainingQty بشكل ثابت أثناء التعديل)
  // =========================
  useEffect(() => {
    // if (isUpdate === false) {

    //   const itemsByInventoryId = (itemsList || [])?.filter((item: any) => item.inventoryId === values.inventoryId);
    //   const itemsInOtherInventories = (itemsList || [])?.filter((item: any) => item.inventoryId !== values.inventoryId);

    //   if (itemsInOtherInventories && itemsInOtherInventories.length > 0) {
    //     setOtherInventoryItemsCount(itemsInOtherInventories.length);
    //     setShowOtherInventoryAlert(true);
    //   } else {
    //     setShowOtherInventoryAlert(false);
    //   }

    //   formik?.setFieldValue('materialTransactionsLineList', itemsByInventoryId, false);
    // }

    if (isUpdate === false) {
      const allItems = itemsList || [];
      const isMOR = values.code === 'MOR'; // استلام مخــزني

      // ✅ MOR: خذ كل الأصناف بدون فلترة inventoryId
      const linesToSet = isMOR ? allItems : allItems.filter((item: any) => item.inventoryId === values.inventoryId);

      // ✅ تنبيه "موجودة في مخازن أخرى" فقط عند عدم MOR
      if (!isMOR) {
        const itemsInOtherInventories = allItems.filter((item: any) => item.inventoryId !== values.inventoryId);

        if (itemsInOtherInventories.length > 0) {
          setOtherInventoryItemsCount(itemsInOtherInventories.length);
          setShowOtherInventoryAlert(true);
        } else {
          setShowOtherInventoryAlert(false);
        }
      } else {
        // في MOR ما نحتاج التنبيه لأننا نعرض الكل
        setShowOtherInventoryAlert(false);
        setOtherInventoryItemsCount(0);
      }

      formik?.setFieldValue('materialTransactionsLineList', linesToSet, false);
    } else {
      const EPS = 1e-9;

      const ItemsListForUpdate = (values.materialTransactionsLineList || []).map((m: any) => {
        const targetItem: any =
          (itemsList || [])?.find(
            (f: any) =>
              String(f.transactionItemId) === String(m.transactionItemId) &&
              String(f.transactionUom || '') === String(m.transactionUom || '')
          ) ?? (itemsList || [])?.find((f: any) => String(f.transactionItemId) === String(m.transactionItemId));

        const realQty = n(targetItem?.RealQty ?? m?.RealQty ?? 0);
        const dispensedTotal = n(targetItem?.dispensedQuantity ?? 0);
        const origDocQty = n(m.__origQty ?? m.transactionQuantityIn);

        const dispensedOther = Math.max(0, dispensedTotal - origDocQty);
        const remainingCap = Math.max(0, realQty - dispensedOther);
        const fullyByOther = dispensedOther >= realQty - EPS;

        return {
          ...m,
          RealQty: realQty,
          dispensedQuantity: dispensedOther,
          RemainingQty: remainingCap,
          IsFullyProcessed: fullyByOther
        };
      });

      formik?.setFieldValue('materialTransactionsLineList', ItemsListForUpdate, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsList, values.inventoryId]);

  // =========================
  // Add / Delete
  // =========================
  const handleAddLine = () => {
    if (!canAddLine) return;

    const newLine: MaterialTransactionsLineList = {
      id: null,
      transactionItemId: null,
      transactionUom: '',
      unitPrice: 0,
      transactionQuantityIn: 0,
      transactionQuantityOut: 0,
      lotId: null,
      inventoryId: '',
      discount: 0,
      codeCombinationId: '',
      currencyCode: '',
      currencyConversionRate: 0,
      referenceIdMaster: '',
      referenceIdLine: '',
      transactionTypeId: '',
      equivalent: 0,
      expensesAmount: 0,
      batchId: ''
    };

    const currentLines = formik.values.materialTransactionsLineList || [];
    formik.setFieldValue('materialTransactionsLineList', [...currentLines, newLine]);
  };

  const handleDeleteLine = (index: number) => {
    // if (!canDeleteLine) return;
    const updatedLines = (values.materialTransactionsLineList || []).filter((_: any, i: number) => i !== index);
    formik.setFieldValue('materialTransactionsLineList', updatedLines);
  };

  // =========================
  // Picker for line
  // =========================
  const openPickerForLine = async (lineIndex: number) => {
    const lines = values.materialTransactionsLineList || [];
    const line = lines[lineIndex];
    if (!line) return;

    if (!values.inventoryId) {
      window?.alert?.(t('يجب اختيار المخزن أولاً.'));
      return;
    }

    if (!canPickItem) return;

    const params: any = { InventoryId: values.inventoryId, Scope: 1 };
    if (values.subInventoryId) params.SubInventoryId = values.subInventoryId;

    const picked = await pickItemUom({
      title: i18n.language === 'en' ? 'Pick Item & UOM' : 'اختيار الصنف',
      params,
      bodyDefaults: { itemType: DEFAULT_ITEM_TYPE },
      pageSize: 10,
      searchMode: 'manual',
      initialSearchField: 'barcodeEqual',
      isDisabledRow: (r: any) => lineHasDuplicateItemUom(r.itemId, r.unitOfMeasure, lineIndex),
      dynamicFields: ['qtyByLot']
    });

    if (!picked) return;

    const row: any = picked.row;
    if (!row?.itemId || !row?.unitOfMeasure) return;

    if (lineHasDuplicateItemUom(row.itemId, row.unitOfMeasure, lineIndex)) {
      window?.alert?.(t('لا يمكن تكرار نفس الصنف بنفس الوحدة.'));
      return;
    }

    setDetailsMap(prev => mergeIntoDetailsMap(prev, [row]));
    ensureItemUomsLoaded(String(row.itemId));

    const updated = [...lines];
    updated[lineIndex] = {
      ...resetLine(updated[lineIndex]),
      transactionItemId: row.itemId,
      transactionUom: row.unitOfMeasure,
      lotId: null
    };

    updated[lineIndex] = clampQty(updated[lineIndex]);
    formik.setFieldValue('materialTransactionsLineList', updated);
  };

  // =========================
  // Line change
  // =========================
  const handleLineChange = (index: number, field: string, value: any) => {
    const updatedLines = [...(values.materialTransactionsLineList || [])];
    if (!updatedLines[index]) return;

    const currentLine = { ...updatedLines[index] };

    if (field === 'clearItem') {
      if (!canPickItem) return;
      updatedLines[index] = resetLine(currentLine);
      formik.setFieldValue('materialTransactionsLineList', updatedLines);
      return;
    }

    if (field === 'transactionUom') {
      if (!canChangeUom) return;

      const itemId = currentLine.transactionItemId;
      if (itemId && value && lineHasDuplicateItemUom(itemId, value, index)) {
        window?.alert?.(t('لا يمكن تكرار نفس الصنف بنفس الوحدة.'));
        return;
      }

      const next = { ...currentLine, transactionUom: value, lotId: null };
      if (itemId) ensureItemUomsLoaded(String(itemId));

      updatedLines[index] = clampQty(next);
      formik.setFieldValue('materialTransactionsLineList', updatedLines);
      return;
    }

    if (field === 'lotId') {
      if (isPosted) return;
      updatedLines[index] = { ...currentLine, lotId: value ?? null };
      formik.setFieldValue('materialTransactionsLineList', updatedLines);
      return;
    }

    if (field === 'transactionQuantityIn') {
      if (!canEditQty) return;
      if (isReferenceReceive && currentLine?.IsFullyProcessed) return;

      const next = { ...currentLine, transactionQuantityIn: n(value) };
      updatedLines[index] = clampQty(next);
      formik.setFieldValue('materialTransactionsLineList', updatedLines);
      return;
    }

    updatedLines[index] = { ...currentLine, [field]: value };
    formik.setFieldValue('materialTransactionsLineList', updatedLines);
  };

  // =========================
  // ✅ Lot Picker (light modal like IssueLines)
  // =========================
  const [lotPickerOpen, setLotPickerOpen] = useState(false);
  const [lotPickerLineIndex, setLotPickerLineIndex] = useState<number | null>(null);
  const [lotPickerRows, setLotPickerRows] = useState<LotRow[]>([]);
  const [lotPickerSelectedLotId, setLotPickerSelectedLotId] = useState<any>(null);
  const [lotPickerItemTitle, setLotPickerItemTitle] = useState<string>('');
  const [lotSearch, setLotSearch] = useState('');

  const openLotPicker = (lineIndex: number, rows: LotRow[], selectedLotId: any, itemTitle: string) => {
    setLotPickerLineIndex(lineIndex);
    setLotPickerRows(rows || []);
    setLotPickerSelectedLotId(selectedLotId ?? null);
    setLotPickerItemTitle(itemTitle || '');
    setLotSearch('');
    setLotPickerOpen(true);
  };

  const closeLotPicker = () => {
    setLotPickerOpen(false);
    setLotPickerLineIndex(null);
    setLotPickerRows([]);
    setLotPickerSelectedLotId(null);
    setLotPickerItemTitle('');
    setLotSearch('');
  };

  const chooseLot = (lotId: any) => {
    if (lotPickerLineIndex == null) return;
    handleLineChange(lotPickerLineIndex, 'lotId', lotId ?? null);
    closeLotPicker();
  };

  const filteredLotRows = useMemo(() => {
    const term = lotSearch.trim().toLowerCase();
    if (!term) return lotPickerRows;

    return (lotPickerRows || []).filter(r =>
      String(r?.lotNumber ?? '')
        .toLowerCase()
        .includes(term)
    );
  }, [lotPickerRows, lotSearch]);

  // =========================
  // UI
  // =========================
  return (
    <>
      {/* ✅ SubInventory */}
      <div className='row m-0'>
        <div className='col-lg-4 mb-1'>
          <div className='input-group flex-nowrap'>
            <span className='input-group-text fw-bold bg-theme bg-opacity-30'>{t('SubInventory')}</span>
            <TreeSelect
              disabled={isPosted}
              loading={loading1}
              showSearch
              className='w-100'
              treeLine
              placeholder={t('Choose')}
              allowClear
              treeDefaultExpandAll={false}
              value={values.subInventoryId}
              onChange={(option: any) => formik.setFieldValue('subInventoryId', option === null ? null : option)}
              treeData={TreeSubInventoryData?.filter((f: any) => f.parentId === values.inventoryId)}
              treeNodeFilterProp='title'
            />
          </div>
        </div>
      </div>

      {/* ✅ other inventory alert (كما كان) */}
      {showOtherInventoryAlert && (
        <div className='d-flex align-items-center justify-content-between bg-secondary bg-opacity-25 rounded p-3 mb-2'>
          <div className='d-flex align-items-center'>
            <span className='fs-3 text-warning fw-bold shadow-lg me-3'>{otherInventoryItemsCount}</span>
            <div>
              <p className='d-block m-0 fw-bold'>موجودة في مخازن أخرى</p>
              <small className='text-dark1'>قم بتغيير المخزن للوصول إليها</small>
            </div>
          </div>
          <button
            type='button'
            className='btn btn-sm btn-outline-secondary'
            onClick={() => setShowOtherInventoryAlert(false)}
          >
            <i className='bi bi-eye-slash me-1'></i>
            إخفاء
          </button>
        </div>
      )}

      {/* ✅ IMPORTANT: لا نخلي detailsLoading يخفي المكون */}
      <Loading loading={loading1} error={errorfetchItems} Type='Dots'>
        <ItemUomPickerModal {...modalProps} />

        {/* ✅ Header (مثل IssueLines) */}
        <div className='card-header'>
          <div className='d-flex flex-column flex-md-row justify-content-between gap-3'>
            <div className='d-flex align-items-center'>
              <i className='fas fa-truck-loading fs-5 text-theme me-2'></i>
              <h6 className='mb-0 fw-semibold'>
                {t('بنود الاستلام المخزني')}
                <span className='badge bg-theme rounded-pill ms-2'>{allLines?.length || 0}</span>
              </h6>

              <span className='badge bg-inverse bg-opacity-25 text-dark ms-2'>
                {isManualReceive
                  ? i18n.language === 'en'
                    ? 'Manual'
                    : 'يدوي'
                  : i18n.language === 'en'
                    ? 'Reference'
                    : 'مرجعي'}
              </span>

              {isPosted && (
                <span className='badge bg-success ms-2'>{i18n.language === 'en' ? 'Posted' : 'مُرحّل'}</span>
              )}
            </div>

            <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2'>
              {(detailsLoading || uomsLoading) && (
                <div className='d-flex align-items-center'>
                  <span className='spinner-border spinner-border-sm text-theme me-2'></span>
                  <small className='text-muted'>
                    {detailsLoading ? t('جاري تحميل التفاصيل...') : t('جاري تحميل الوحدات...')}
                  </small>
                </div>
              )}

              {canAddLine && (
                <button
                  type='button'
                  className='btn btn-theme btn-sm d-flex align-items-center justify-content-center px-3 w-100 w-md-auto'
                  onClick={handleAddLine}
                >
                  <i className='fas fa-plus me-2'></i>
                  <span>{t('AddItem')}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ✅ Table */}
        <div className='card-body p-0'>
          <div
            className='table-responsive'
            style={{ maxHeight: '350px', overflowY: 'auto', overflowX: 'auto', position: 'relative' }}
          >
            <table className='table table-hover mb-0' style={{ minWidth: '1000px' }}>
              <thead
                className='shadow fw-bold'
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 101,
                  backdropFilter: 'blur(30px)'
                }}
              >
                <tr>
                  <th
                    className='text-center small shadow'
                    style={{ width: '50px', minWidth: '50px', position: 'sticky', left: 0, zIndex: 102 }}
                  >
                    #
                  </th>

                  <th className='text-center small' style={{ width: '400px', minWidth: '400px' }}>
                    {t('ItemName')}
                  </th>

                  <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
                    {t('Unit')}
                  </th>

                  <th className='text-center small' style={{ width: '140px', minWidth: '140px' }}>
                    {t('LotNumber')}
                  </th>

                  <th className='text-center small' style={{ width: '80px', minWidth: '80px' }}>
                    {t('Quantity')}
                  </th>

                  {showUnitPrice && (
                    <th className='text-center small' style={{ width: '90px', minWidth: '90px' }}>
                      {t('UnitPrice')}
                    </th>
                  )}

                  <th className='text-center small' style={{ width: '70px', minWidth: '70px' }}></th>
                </tr>
              </thead>

              <tbody className='fw-bold'>
                {visibleLines?.length > 0 ? (
                  visibleLines.map((line: any, idx: number) => {
                    const Index = idx;

                    const infoExact = getInfoExact(line);
                    const infoLoose = infoExact || getInfoAnyByItemId(line?.transactionItemId);

                    const itemName = line.transactionItemId ? getItemNameForLine(line) : '';
                    const uomName = line.transactionItemId ? getUomNameForLine(line) : '';
                    const codeOrBarcode = line.transactionItemId ? getCodeOrBarcodeForLine(line) : '-';

                    const isDuplicate =
                      !readOnlyByCode &&
                      line.transactionItemId &&
                      line.transactionUom &&
                      (values.materialTransactionsLineList || []).some(
                        (l: any, i: number) =>
                          i !== Index &&
                          String(l?.transactionItemId || '') === String(line.transactionItemId) &&
                          String(l?.transactionUom || '') === String(line.transactionUom)
                      );

                    const uomOptions = line.transactionItemId
                      ? uomsByItemId.get(String(line.transactionItemId)) || []
                      : [];

                    // lots from qtyByLot + added lot for item
                    const lots = Array.isArray(infoExact?.qtyByLot) ? infoExact.qtyByLot : [];
                    const addedLot = line.transactionItemId ? addedLots[String(line.transactionItemId)] : null;

                    const lotRows: LotRow[] = [
                      { lotId: null, lotNumber: i18n.language === 'en' ? 'None' : 'بدون' },
                      ...lots.map((x: any) => ({
                        lotId: x?.lotId ?? x?.id,
                        lotNumber: x?.lotNumber,
                        expirationDate: x?.expirationDate ?? null
                      }))
                    ];

                    if (addedLot && Object.keys(addedLot).length > 0) {
                      // ensure it appears (if not already)
                      const exists = lotRows.some(r => String(r.lotId ?? '') === String(addedLot.id ?? ''));
                      if (!exists) {
                        lotRows.push({
                          lotId: addedLot.id,
                          lotNumber: addedLot.lotNumber,
                          expirationDate: addedLot.expirationDate
                        });
                      }
                    }

                    const selectedLot =
                      lotRows.find(x => String(x?.lotId ?? '') === String(line.lotId ?? '')) ?? lotRows[0];

                    const maxAllowed = getMaxAllowedQty(line);
                    const qtyDisabled = !canEditQty || (isReferenceReceive && line?.IsFullyProcessed);
                    const displayNo = Index + 1;

                    return (
                      <tr key={line?.id ?? Index}>
                        {/* ✅ sticky # */}
                        <td
                          className='text-center align-middle bg-body shadow'
                          style={{ position: 'sticky', left: 0, zIndex: 100 }}
                        >
                          <span className='badge bg-theme bg-opacity-80 fw-bold' style={{ fontSize: '0.85rem' }}>
                            {displayNo}
                          </span>
                        </td>

                        {/* ✅ Item cell */}
                        <td className='align-middle'>
                          <div style={{ width: '100%' }}>
                            <div className={`p-2 border ${isDuplicate ? 'border-danger' : 'border-theme'} bg-body`}>
                              <div className='d-flex align-items-start gap-2'>
                                <div className='flex-grow-1' style={{ minWidth: 0 }}>
                                  {line.transactionItemId ? (
                                    <>
                                      <span className='fw-bold text-truncate d-block'>
                                        {itemName ? (
                                          itemName
                                        ) : detailsLoading ? (
                                          <span className='text-muted'>
                                            <span className='spinner-border spinner-border-sm me-2'></span>
                                            {i18n.language === 'en' ? 'Loading...' : 'جاري التحميل...'}
                                          </span>
                                        ) : (
                                          `ID: ${line.transactionItemId}`
                                        )}

                                        {line?.IsFullyProcessed && (
                                          <span className='badge bg-success ms-2'>
                                            {i18n.language === 'en' ? 'Processed' : 'مكتمل'}
                                          </span>
                                        )}
                                      </span>

                                      <small className='text-muted d-block'>
                                        {t('Unit')}: <span className='fw-bold text-theme'>{uomName || '-'}</span>
                                        <span className='mx-2'>•</span>
                                        {t('ItemCode')}:{' '}
                                        <span className='fw-bold'>{String(codeOrBarcode).slice(0, 40)}</span>
                                      </small>
                                    </>
                                  ) : (
                                    <span className='text-muted'>{t('Choose')}</span>
                                  )}

                                  {isDuplicate && (
                                    <small className='text-danger d-block mt-1'>
                                      <i className='fas fa-exclamation-triangle me-1'></i>
                                      {t('هذا الصنف مكرر مع نفس الوحدة')}
                                    </small>
                                  )}
                                </div>

                                {/* Actions */}
                                <div className='d-flex gap-1'>
                                  <button
                                    disabled={!canPickItem || !values.inventoryId}
                                    type='button'
                                    className='btn btn-sm btn-theme rounded-0'
                                    onClick={() => openPickerForLine(Index)}
                                    title={!values.inventoryId ? t('يجب اختيار المخزن أولاً.') : t('اختيار صنف ووحدة')}
                                  >
                                    <i className='fas fa-search me-1'></i>
                                    {line.transactionItemId ? t('تغيير') : t('اختيار')}
                                  </button>

                                  {line.transactionItemId && (
                                    <button
                                      disabled={!canPickItem}
                                      type='button'
                                      className='btn btn-sm btn-outline-danger rounded-0'
                                      onClick={() => handleLineChange(Index, 'clearItem', true)}
                                      title={t('مسح الصنف')}
                                    >
                                      <i className='fas fa-times'></i>
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* ✅ UOM */}
                        <td className='align-middle'>
                          <select
                            disabled={!canChangeUom || !line.transactionItemId}
                            className={`form-select form-select-sm bg-body ${
                              isDuplicate ? 'border-danger' : 'border-theme'
                            }`}
                            value={line.transactionUom || ''}
                            onFocus={() => {
                              if (line.transactionItemId) ensureItemUomsLoaded(String(line.transactionItemId));
                            }}
                            onChange={e => handleLineChange(Index, 'transactionUom', e.target.value)}
                            style={{ width: '100%' }}
                          >
                            <option value=''>{t('Choose')}</option>

                            {line.transactionUom && uomOptions.length === 0 && (
                              <option value={line.transactionUom}>{uomName || line.transactionUom}</option>
                            )}

                            {uomOptions.map((u: any) => {
                              const uomVal = u.unitOfMeasure;
                              const label =
                                (i18n.language === 'en'
                                  ? u.unitOfMeasureName || u.unitOfMeasureName2
                                  : u.unitOfMeasureName2 || u.unitOfMeasureName) || `ID: ${uomVal}`;

                              return (
                                <option key={uomVal} value={uomVal}>
                                  {label}
                                </option>
                              );
                            })}

                            {uomsLoading && line.transactionItemId ? (
                              <option value={line.transactionUom || ''} disabled>
                                {i18n.language === 'en' ? 'Loading...' : '...جاري التحميل'}
                              </option>
                            ) : null}
                          </select>
                        </td>

                        {/* ✅ Lot (light picker + add lot) */}
                        <td className='align-middle'>
                          {(() => {
                            const disabled = isPosted || !line.transactionItemId;
                            const lotLabel = selectedLot?.lotNumber || (i18n.language === 'en' ? 'None' : 'بدون');

                            return (
                              <div className='d-flex flex-column gap-1'>
                                <div
                                  className='input-group input-group-sm border border-theme'
                                  onClick={() => {
                                    if (disabled) return;
                                    openLotPicker(
                                      Index,
                                      lotRows as any,
                                      line.lotId,
                                      itemName || `ID: ${line.transactionItemId}`
                                    );
                                  }}
                                  style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                                >
                                  <input
                                    readOnly
                                    className='form-control form-control-sm bg-body'
                                    value={lotLabel}
                                    placeholder={t('LotNumber')}
                                    disabled={disabled}
                                  />
                                  <span className='input-group-text border-0 bg-body'>
                                    <i className='fas fa-caret-down'></i>
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </td>

                        {/* ✅ Qty */}
                        <td className='align-middle'>
                          <PopoverCustom
                            childern={
                              <input
                                type='number'
                                autoComplete='off'
                                className='form-control form-control-sm text-center px-0 border-theme bg-body'
                                value={line.transactionQuantityIn}
                                disabled={qtyDisabled}
                                onChange={e => handleLineChange(Index, 'transactionQuantityIn', Number(e.target.value))}
                                onInput={e => {
                                  if (isPosted) return;

                                  const input = e.target as HTMLInputElement;
                                  input.value = input.value?.replace(/[^0-9]/g, '');

                                  const entered = Number(input.value);
                                  if (!Number.isFinite(maxAllowed)) return;

                                  if (entered > maxAllowed) input.value = String(maxAllowed);
                                }}
                              />
                            }
                            Header={
                              isReferenceReceive
                                ? i18n.language === 'en'
                                  ? `Max Allowed: ${Number.isFinite(maxAllowed) ? maxAllowed : '∞'}`
                                  : `${t('الحد المسموح')}: ${Number.isFinite(maxAllowed) ? maxAllowed : '∞'}`
                                : i18n.language === 'en'
                                  ? 'Manual receive'
                                  : 'استلام يدوي'
                            }
                            Body={
                              !line?.RemainingQty && !line?.dispensedQuantity ? (
                                <div className='text-muted small'>
                                  {i18n.language === 'en' ? 'No reference quantities.' : 'لا توجد كميات مرجعية.'}
                                </div>
                              ) : (
                                <div className='d-flex flex-column gap-1'>
                                  <div className='d-flex justify-content-between'>
                                    <span>{t('AddedQuantity')}</span>
                                    <span className='fw-bold text-danger'>{n(line.dispensedQuantity)}</span>
                                  </div>
                                  <div className='d-flex justify-content-between'>
                                    {line.IsFullyProcessed ? (
                                      <span className='text-success fw-bold'>{t('FullyProcessed')}</span>
                                    ) : (
                                      <>
                                        <span>{t('RemainingQuantity')}</span>
                                        <span className='fw-bold text-success'>{n(line.RemainingQty)}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              )
                            }
                          />
                        </td>
                        {showUnitPrice && (
                          <td className='align-middle'>
                            <input
                              type='number'
                              step='0.000001'
                              autoComplete='off'
                              className='form-control form-control-sm text-center px-1 border-theme bg-body'
                              value={n(line.unitPrice)}
                              disabled
                            />
                          </td>
                        )}

                        {/* ✅ Delete */}
                        <td className='align-middle text-center'>
                          {/* {canDeleteLine ? ( */}
                          {true ? (
                            <button
                              type='button'
                              className='btn btn-sm btn-danger rounded-0'
                              onClick={() => handleDeleteLine(Index)}
                              title={t('Delete Line')}
                              style={{ width: '28px', height: '28px', padding: '0' }}
                            >
                              <i className='fas fa-trash text-white'></i>
                            </button>
                          ) : (
                            <span className='text-muted'>-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className='text-center py-4 text-muted'>
                      <i className='fa fa-inbox fa-2x mb-2'></i>
                      <br />
                      {t('لا توجد أصناف مضافة')}
                      <br />
                      {canAddLine && (
                        <button type='button' className='btn btn-theme mt-3' onClick={handleAddLine}>
                          <i className='fas fa-plus me-2'></i>
                          {t('إضافة اول صنف')}
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ✅ Show more (لودنج على الزر فقط) */}
          {hasMoreLines && remainingToShow > 0 && (
            <div className='p-2 text-center'>
              <button type='button' className='btn btn-outline-secondary btn-sm' onClick={showMore}>
                {loadingMore ? (
                  <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                ) : (
                  <i className='fas fa-chevron-down me-2'></i>
                )}

                {loadingMore
                  ? i18n.language === 'en'
                    ? 'Loading...'
                    : 'جاري التحميل...'
                  : i18n.language === 'en'
                    ? `Show more (${remainingToShow})`
                    : `عرض المزيد (${remainingToShow})`}
              </button>

              <div className='text-muted small mt-1'>
                {i18n.language === 'en'
                  ? `Showing ${Math.min(visibleCount, allLines.length)} of ${allLines.length}`
                  : `المعروض ${Math.min(visibleCount, allLines.length)} من ${allLines.length}`}
              </div>
            </div>
          )}

          {Array.isArray(formik.errors.materialTransactionsLineList) && (
            <div className='bg-danger-subtle p-2 text-danger text-center small fw-bold'>
              {' '}
              {t('يرجى التحقق من صحة بيانات البنود ')}{' '}
            </div>
          )}

          {/* {formik.errors.materialTransactionsLineList?.length ? <div className='text-danger p-2'> * مطلوب </div> : null} */}
        </div>

        {/* ✅ Card Footer */}
        <div className='card-footer d-flex justify-content-between align-items-center'>
          <div className='text-muted small'>
            <i className='fas fa-list me-1'></i>
            {t('عدد الأصناف')}: <span className='fw-bold'>{allLines?.length || 0}</span>
          </div>

          <div className='text-theme fw-bold'>
            <i className='fas fa-info-circle me-2'></i>
            {isPosted ? (i18n.language === 'en' ? 'Read only (Posted)' : 'قراءة فقط (مُرحّل)') : '-'}
          </div>
        </div>
      </Loading>

      {/* ✅ Lot Picker Modal */}
      <NeatixModal
        show={lotPickerOpen}
        setShow={setLotPickerOpen}
        size='md'
        bodyClassName='p-0'
        showHeader={false}
        centered
      >
        <Card>
          <CardBody>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <div className='fw-bold'>{i18n.language === 'en' ? 'Select Lot' : 'اختيار اللوت'}</div>
              <button type='button' className='btn btn-sm btn-outline-secondary' onClick={closeLotPicker}>
                <i className='bi bi-x'></i>
              </button>
            </div>

            <div className='text-muted small mb-2'>{lotPickerItemTitle}</div>

            <input
              className='form-control form-control-sm mb-2'
              placeholder={i18n.language === 'en' ? 'Search lot...' : 'ابحث عن لوت...'}
              value={lotSearch}
              onChange={e => setLotSearch(e.target.value)}
            />

            <div className='d-flex gap-2 mb-2'>
              <button type='button' className='btn btn-sm btn-outline-secondary w-100' onClick={() => chooseLot(null)}>
                {i18n.language === 'en' ? 'None' : 'بدون'}
              </button>

              <button
                type='button'
                className='btn btn-sm btn-theme w-100'
                onClick={() => {
                  // open AddLotItem for current selected line
                  const idx = lotPickerLineIndex;
                  if (idx == null) return;
                  const line = (values.materialTransactionsLineList || [])[idx];
                  if (!line?.transactionItemId) return;
                  setLineData(line);
                  setShowAddLotModal(true);
                }}
              >
                <i className='fas fa-plus me-1'></i>
                {t('NewLotNumber')}
              </button>
            </div>

            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {filteredLotRows.length === 0 ? (
                <div className='text-muted small text-center py-3'>
                  {i18n.language === 'en' ? 'No lots' : 'لا توجد لوتات'}
                </div>
              ) : (
                <div className='list-group'>
                  {filteredLotRows
                    .filter(r => r?.lotId != null) // exclude "None" here, already button
                    .map((r, i) => {
                      const active = String(r?.lotId ?? '') === String(lotPickerSelectedLotId ?? '');
                      return (
                        <button
                          key={`${r?.lotId}-${i}`}
                          type='button'
                          className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                            active ? 'active' : ''
                          }`}
                          onClick={() => chooseLot(r?.lotId)}
                        >
                          <span className='fw-bold'>{r?.lotNumber || '-'}</span>
                          <small className={active ? 'text-white-50' : 'text-muted'}>
                            {r?.expirationDate ? String(r.expirationDate).slice(0, 10) : ''}
                          </small>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </NeatixModal>

      {/* ✅ Add Lot Modal */}
      <NeatixModal
        show={showAddLotModal}
        setShow={setShowAddLotModal}
        size='md'
        bodyClassName='p-0'
        showHeader={false}
        centered
      >
        <Card>
          <CardBody>
            {lineData && (
              <AddLotItem lineData={lineData} onClose={() => setShowAddLotModal(false)} setAddedLots={setAddedLots} />
            )}
          </CardBody>
        </Card>
      </NeatixModal>
    </>
  );
};

export default ReceiveLines;

// import React, { useEffect, useMemo, useRef, useState } from 'react';
// import { useTranslation } from 'react-i18next';
// import { type FormikProps } from 'formik';
// import Loading from '@components/Shared/Loader/Loading';
// import PopoverCustom from '@components/Shared/Popover/PopoverCustom';
// import type { MaterialTransactionsLineList } from '@store/INV/Transactions/SharedModels';
// import NeatixModal from '@components/Modal/NeatixModal';
// import { Card, CardBody } from '@components/card/card';
// import AddLotItem from '../../INVSettings/MasterItems/MasterItemsDetails/LotItems/AddLotItem';
// import { useAppDispatch, useAppSelector } from '@store/hooks';
// import { fetchDataByReferenceId, cleanUpItemsList } from '@store/INV/Transactions/InvIssue/invSlice';
// import useGetSubinventoryList from '@hooks/INV/Inventories/useGetSubinventoryList';
// import { TreeSelect } from 'antd';

// // ✅ Picker
// import { useItemUomPicker, ItemUomPickerModal } from '@pages/INV/lookups/useItemUomPicker';

// // ✅ API (lookup) - same as IssueLines
// import {
//   lookupItemsWithUomRowsByItemIds,
//   mergeIntoDetailsMap,
//   makeDetailsKey
// } from '@pages/INV/lookups/itemsWithUomLookup.logic';

// interface LotItem {
//   id: string;
//   lotNumber: string;
//   expirationDate?: string;
//   itemId?: string;
// }

// interface LinesProps {
//   formik: FormikProps<any>;
//   isUpdate?: boolean;
// }

// type ItemType = 'InventoryItem';
// const DEFAULT_ITEM_TYPE: ItemType = 'InventoryItem';

// const n = (v: any) => {
//   const x = Number(v);
//   return Number.isFinite(x) ? x : 0;
// };

// const PAGE_SIZE = 20;

// type LotRow = {
//   lotId: any; // null => None
//   lotNumber?: string | null;
//   expirationDate?: string | null;
// };

// const ReceiveLines: React.FC<LinesProps> = ({ formik, isUpdate = false }) => {
//   const { TreeSubInventoryData } = useGetSubinventoryList();
//   const { values, setFieldValue } = formik;
//   const { i18n, t } = useTranslation();

//   const dispatch = useAppDispatch();
//   const { itemsList, loading: loading1, error: errorfetchItems } = useAppSelector(state => state.Inv);

//   const isPosted = values.postedFlag === true;

//   // ✅ Manual Receive codes
//   const MANUAL_RECEIVE_CODES = ['MIR'];
//   const isManualReceive = MANUAL_RECEIVE_CODES.includes(values.code);
//   const isReferenceReceive = !isManualReceive;

//   const readOnlyByCode = isReferenceReceive;

//   const canPickItem = !isPosted && !readOnlyByCode;
//   const canChangeUom = !isPosted && !readOnlyByCode;
//   const canAddLine = !isPosted && !readOnlyByCode;
//   const canDeleteLine = !isPosted && !readOnlyByCode;
//   const canEditQty = !isPosted;

//   // ✅ Picker
//   const { pickItemUom, modalProps } = useItemUomPicker();

//   // ✅ detailsMap (same pattern as IssueLines)
//   const [detailsMap, setDetailsMap] = useState<Map<string, any>>(new Map());
//   const detailsMapRef = useRef(detailsMap);
//   useEffect(() => {
//     detailsMapRef.current = detailsMap;
//   }, [detailsMap]);

//   // ✅ fast access by itemId (avoid staying as ID)
//   const anyRowByItemId = useMemo(() => {
//     const m = new Map<string, any>();
//     for (const r of detailsMap.values()) {
//       if (!r?.itemId) continue;
//       const k = String(r.itemId);
//       if (!m.has(k)) m.set(k, r);
//     }
//     return m;
//   }, [detailsMap]);
//   const anyRowByItemIdRef = useRef(anyRowByItemId);
//   useEffect(() => {
//     anyRowByItemIdRef.current = anyRowByItemId;
//   }, [anyRowByItemId]);

//   const [detailsLoading, setDetailsLoading] = useState(false);
//   const detailsAbortRef = useRef<AbortController | null>(null);

//   // ✅ smooth debounce + request sequencing
//   const detailsTimerRef = useRef<any>(null);
//   const detailsReqSeqRef = useRef(0);
//   const prevVisibleCountRef = useRef(PAGE_SIZE);

//   // ✅ Fully processed count (UI فقط) - لا يدخل في Formik list
//   const [processedRefCount, setProcessedRefCount] = useState(0);

//   // ✅ "loading on button" only
//   const [loadingMore, setLoadingMore] = useState(false);

//   // ✅ UOM preload
//   const loadedUomsRef = useRef(new Set<string>());
//   const uomsAbortRef = useRef<AbortController | null>(null);
//   const [uomsLoading, setUomsLoading] = useState(false);

//   // ✅ Lot add modal (AddLotItem)
//   const [showAddLotModal, setShowAddLotModal] = useState(false);
//   const [lineData, setLineData] = useState<any>();
//   const [addedLots, setAddedLots] = useState<Record<string, LotItem | null>>({});

//   // ✅ Other inventory alert
//   const [showOtherInventoryAlert, setShowOtherInventoryAlert] = useState(false);
//   const [otherInventoryItemsCount, setOtherInventoryItemsCount] = useState(0);

//   // =========================
//   // ✅ Pagination (NO reverse)
//   // =========================
//   const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

//   // useEffect(() => {
//   //   setVisibleCount(PAGE_SIZE);
//   //   setLoadingMore(false);
//   //   prevVisibleCountRef.current = PAGE_SIZE;
//   //   // eslint-disable-next-line react-hooks/exhaustive-deps
//   // }, [values.inventoryId, values.subInventoryId, values.referenceId, values.code]);

//   useEffect(() => {
//     setVisibleCount(PAGE_SIZE);
//     setLoadingMore(false);
//     prevVisibleCountRef.current = PAGE_SIZE;

//     // ✅ reset processed count
//     setProcessedRefCount(0);

//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [values.inventoryId, values.subInventoryId, values.referenceId, values.code]);

//   const allLines: any[] = values.materialTransactionsLineList || [];

//   const visibleLines: any[] = useMemo(() => {
//     return allLines.slice(0, visibleCount);
//   }, [allLines, visibleCount]);

//   const hasMoreLines = allLines.length > visibleCount;
//   const remainingToShow = Math.max(0, Math.min(PAGE_SIZE, allLines.length - visibleCount));

//   const showMore = () => {
//     setVisibleCount(v => Math.min(v + PAGE_SIZE, allLines.length));
//   };

//   // =========================
//   // Helpers
//   // =========================
//   const resetLine = (line: any) => ({
//     ...line,
//     transactionItemId: null,
//     transactionUom: '',
//     lotId: null,
//     transactionQuantityIn: 0,
//     transactionQuantityOut: 0,
//     unitPrice: 0,
//     total: 0
//   });

//   const getInfoExact = (line: any) => {
//     if (!line?.transactionItemId || !line?.transactionUom) return null;
//     return detailsMap.get(makeDetailsKey(line.transactionItemId, line.transactionUom)) || null;
//   };

//   const getInfoAnyByItemId = (itemId: any) => {
//     if (!itemId) return null;
//     return anyRowByItemIdRef.current.get(String(itemId)) || null;
//   };

//   const getInfoLoose = (line: any) => {
//     const exact = getInfoExact(line);
//     if (exact) return exact;
//     return getInfoAnyByItemId(line?.transactionItemId);
//   };

//   const getItemNameForLine = (line: any) => {
//     const info = getInfoLoose(line);
//     const isEn = i18n.language === 'en';
//     const name = (isEn ? info?.name || info?.name2 : info?.name2 || info?.name) || '';
//     if (name) return name;

//     // fallback from line if exists
//     return (
//       (isEn
//         ? line?.itemName || line?.transactionItemName || line?.itemName2 || line?.transactionItemName2
//         : line?.itemName2 || line?.transactionItemName2 || line?.itemName || line?.transactionItemName) || ''
//     );
//   };

//   const getUomNameForLine = (line: any) => {
//     const info = getInfoLoose(line);
//     const isEn = i18n.language === 'en';
//     return (
//       (isEn
//         ? info?.unitOfMeasureName || info?.unitOfMeasureName2
//         : info?.unitOfMeasureName2 || info?.unitOfMeasureName) || ''
//     );
//   };

//   const getCodeOrBarcodeForLine = (line: any) => {
//     const info = getInfoLoose(line);
//     return info?.barcode || info?.itemCode || line?.itemCode || line?.barcode || '-';
//   };

//   const lineHasDuplicateItemUom = (itemId: any, uom: any, excludeIndex: number) => {
//     if (!itemId || !uom) return false;
//     return (values.materialTransactionsLineList || []).some((l: any, i: number) => {
//       if (i === excludeIndex) return false;
//       return String(l?.transactionItemId || '') === String(itemId) && String(l?.transactionUom || '') === String(uom);
//     });
//   };

//   const getMaxAllowedQty = (line: any) => {
//     // ✅ Receive: manual => ∞, reference => RemainingQty (إن وجدت)
//     if (isManualReceive) return Number.POSITIVE_INFINITY;
//     const remaining = line?.RemainingQty != null ? Math.max(0, n(line.RemainingQty)) : 0;
//     return remaining;
//   };

//   const clampQty = (line: any) => {
//     const max = getMaxAllowedQty(line);
//     if (!Number.isFinite(max)) return line;
//     const q = n(line.transactionQuantityIn);
//     if (q <= max) return line;
//     return { ...line, transactionQuantityIn: max };
//   };

//   // =========================
//   // ✅ UOM options per itemId (from detailsMap only)
//   // =========================
//   const uomsByItemId = useMemo(() => {
//     const map = new Map<string, any[]>();

//     for (const r of detailsMap.values()) {
//       if (!r?.itemId) continue;
//       const list = map.get(String(r.itemId)) ?? [];
//       list.push(r);
//       map.set(String(r.itemId), list);
//     }

//     for (const [itemId, list] of map.entries()) {
//       const uniq = new Map<string, any>();
//       for (const r of list) uniq.set(String(r.unitOfMeasure), r);

//       const arr = Array.from(uniq.values());
//       arr.sort((a, b) => Number(!!b.isPrimaryUOM) - Number(!!a.isPrimaryUOM));
//       map.set(itemId, arr);
//     }

//     return map;
//   }, [detailsMap]);

//   // =========================
//   // ✅ fetch UOMs for item (include qtyByLot)
//   // =========================
//   const ensureItemUomsLoaded = async (itemId: string) => {
//     if (!values.inventoryId || !itemId) return;

//     const key = `${values.inventoryId}|${values.subInventoryId || ''}|${DEFAULT_ITEM_TYPE}|${itemId}`;
//     if (loadedUomsRef.current.has(key)) return;
//     loadedUomsRef.current.add(key);

//     try {
//       uomsAbortRef.current?.abort();
//       uomsAbortRef.current = new AbortController();
//       setUomsLoading(true);

//       const params: any = { InventoryId: values.inventoryId, Scope: 1 };
//       if (values.subInventoryId) params.SubInventoryId = values.subInventoryId;

//       const rows = await lookupItemsWithUomRowsByItemIds({
//         params,
//         itemType: DEFAULT_ITEM_TYPE,
//         itemIds: [itemId],
//         pageSize: 3000,
//         pageIndex: 1,
//         chunkSize: 200,
//         bodyExtra: {
//           dynamicFields: ['qtyByLot']
//         },
//         opts: {
//           signal: uomsAbortRef.current?.signal,
//           cache: true,
//           dedupeInFlight: true
//         }
//       });

//       if (!rows.length) return;

//       setDetailsMap(prev => mergeIntoDetailsMap(prev, rows));
//     } catch (e: any) {
//       loadedUomsRef.current.delete(key);
//       if (e?.name !== 'CanceledError') console.error(e);
//     } finally {
//       setUomsLoading(false);
//     }
//   };

//   // =========================
//   // ✅ preload details for VISIBLE lines only
//   // ✅ + debounce
//   // ✅ + Abort on rapid show more
//   // ✅ + loading only on button
//   // =========================
//   const visibleLinesKey = useMemo(() => {
//     return visibleLines.map((l: any) => `${l?.transactionItemId || ''}:${l?.transactionUom || ''}`).join('|');
//   }, [visibleLines]);

//   useEffect(() => {
//     const isLoadMore = visibleCount > prevVisibleCountRef.current;
//     prevVisibleCountRef.current = visibleCount;

//     if (isLoadMore) setLoadingMore(true);

//     if (detailsTimerRef.current) clearTimeout(detailsTimerRef.current);

//     detailsAbortRef.current?.abort();
//     detailsAbortRef.current = null;

//     detailsTimerRef.current = setTimeout(async () => {
//       if (!values.inventoryId) {
//         if (isLoadMore) setLoadingMore(false);
//         return;
//       }

//       const lines = visibleLines || [];
//       const valid = lines.filter((l: any) => l?.transactionItemId && l?.transactionUom);
//       if (valid.length === 0) {
//         if (isLoadMore) setLoadingMore(false);
//         return;
//       }

//       const mapNow = detailsMapRef.current;
//       const missingItemIds = new Set<string>();

//       for (const l of valid) {
//         const k = makeDetailsKey(l.transactionItemId, l.transactionUom);
//         if (!mapNow.has(k)) missingItemIds.add(String(l.transactionItemId));
//       }

//       if (missingItemIds.size === 0) {
//         if (isLoadMore) setLoadingMore(false);
//         return;
//       }

//       const seq = ++detailsReqSeqRef.current;
//       const thisIsLoadMore = isLoadMore;

//       detailsAbortRef.current?.abort();
//       detailsAbortRef.current = new AbortController();

//       setDetailsLoading(true);

//       try {
//         const params: any = { InventoryId: values.inventoryId, Scope: 1 };
//         if (values.subInventoryId) params.SubInventoryId = values.subInventoryId;

//         const ids = Array.from(missingItemIds);

//         const rows = await lookupItemsWithUomRowsByItemIds({
//           params,
//           itemType: DEFAULT_ITEM_TYPE,
//           itemIds: ids,
//           pageSize: 3000,
//           pageIndex: 1,
//           chunkSize: 200,
//           bodyExtra: {
//             dynamicFields: ['qtyByLot']
//           },
//           opts: {
//             signal: detailsAbortRef.current?.signal,
//             cache: true,
//             dedupeInFlight: true
//           }
//         });

//         setDetailsMap(prev => mergeIntoDetailsMap(prev, rows));
//       } catch (e: any) {
//         if (e?.name !== 'CanceledError') console.error(e);
//       } finally {
//         if (seq === detailsReqSeqRef.current) {
//           setDetailsLoading(false);
//           if (thisIsLoadMore) setLoadingMore(false);
//         }
//       }
//     }, 150);

//     return () => {
//       if (detailsTimerRef.current) clearTimeout(detailsTimerRef.current);
//       detailsAbortRef.current?.abort();
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [values.inventoryId, values.subInventoryId, visibleLinesKey, visibleCount]);

//   // ✅ clear caches when inventory/subInventory changes
//   useEffect(() => {
//     setDetailsMap(new Map());
//     loadedUomsRef.current.clear();
//   }, [values.inventoryId, values.subInventoryId]);

//   // =========================
//   // Reference fetch (كما هو)
//   // =========================
//   const updateFieldsBasedOnCode = (data: any) => {
//     setFieldValue('referenceCode', data?.data?.code ?? data?.data?.orderCode);

//     switch (values.code) {
//       case 'INV':
//         setFieldValue('customerId', data?.data?.peopleId ?? null);
//         setFieldValue('inventoryId', data?.data?.inventoryId);
//         break;
//       case 'SOI':
//         setFieldValue('customerId', data?.data?.peopleId ?? null);
//         setFieldValue('supplierId', null);
//         break;
//       case 'IOI':
//         setFieldValue('subInventoryId', data?.data?.subInventoryId);
//         setFieldValue('inventoryId', data?.data?.inventoryId);
//         break;
//       case 'SPR':
//         setFieldValue('supplierId', data?.data?.peopleId ?? null);
//         setFieldValue('customerId', null);
//         break;
//       case 'POR':
//         setFieldValue('supplierId', data?.data?.peopleId ?? null);
//         setFieldValue('customerId', null);
//         break;
//       case 'BIL':
//         setFieldValue('supplierId', data?.data?.peopleId);
//         setFieldValue('customerId', null);
//         break;
//       case 'CRT':
//         setFieldValue('supplierId', null);
//         setFieldValue('customerId', data?.data?.peopleId);
//         break;
//       case 'IOR':
//         setFieldValue('branchId', data?.data?.branchId);
//         break;
//       default:
//         break;
//     }
//   };

//   useEffect(() => {
//     if (['MII', 'MIR'].includes(values.code)) return;

//     if (values.referenceId) {
//       dispatch(fetchDataByReferenceId({ referenceId: values.referenceId, code: values.code }))
//         .unwrap()
//         .then(({ data }) => updateFieldsBasedOnCode(data))
//         .catch(err => console.error('Failed to fetch data:', err));
//     }

//     return () => {
//       dispatch(cleanUpItemsList());
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [values.referenceId, values.code]);

//   // =========================
//   // ✅ Update mode stamp + stable RemainingQty calc (مثل IssueLines)
//   // =========================
//   useEffect(() => {
//     if (!isUpdate) return;

//     const lines = values.materialTransactionsLineList || [];
//     if (!lines.length) return;

//     const needStamp = lines.some((l: any) => l.__origQty == null);
//     if (!needStamp) return;

//     const patched = lines.map((l: any) => ({
//       ...l,
//       __origQty: l.__origQty ?? n(l.transactionQuantityIn)
//     }));

//     formik.setFieldValue('materialTransactionsLineList', patched, false);
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [isUpdate]);

//   // =========================
//   // itemsList -> lines (مع alert + تحديث RemainingQty بشكل ثابت أثناء التعديل)
//   // =========================
//   // useEffect(() => {
//   //   if (isUpdate === false) {
//   //     const itemsByInventoryId = (itemsList || [])?.filter((item: any) => item.inventoryId === values.inventoryId);
//   //     const itemsInOtherInventories = (itemsList || [])?.filter((item: any) => item.inventoryId !== values.inventoryId);

//   //     if (itemsInOtherInventories && itemsInOtherInventories.length > 0) {
//   //       setOtherInventoryItemsCount(itemsInOtherInventories.length);
//   //       setShowOtherInventoryAlert(true);
//   //     } else {
//   //       setShowOtherInventoryAlert(false);
//   //     }

//   //     formik?.setFieldValue('materialTransactionsLineList', itemsByInventoryId, false);
//   //   } else {
//   //     const EPS = 1e-9;

//   //     const ItemsListForUpdate = (values.materialTransactionsLineList || []).map((m: any) => {
//   //       const targetItem: any =
//   //         (itemsList || [])?.find(
//   //           (f: any) =>
//   //             String(f.transactionItemId) === String(m.transactionItemId) &&
//   //             String(f.transactionUom || '') === String(m.transactionUom || '')
//   //         ) ?? (itemsList || [])?.find((f: any) => String(f.transactionItemId) === String(m.transactionItemId));

//   //       const realQty = n(targetItem?.RealQty ?? m?.RealQty ?? 0);
//   //       const dispensedTotal = n(targetItem?.dispensedQuantity ?? 0);
//   //       const origDocQty = n(m.__origQty ?? m.transactionQuantityIn);

//   //       const dispensedOther = Math.max(0, dispensedTotal - origDocQty);
//   //       const remainingCap = Math.max(0, realQty - dispensedOther);
//   //       const fullyByOther = dispensedOther >= realQty - EPS;

//   //       return {
//   //         ...m,
//   //         RealQty: realQty,
//   //         dispensedQuantity: dispensedOther,
//   //         RemainingQty: remainingCap,
//   //         IsFullyProcessed: fullyByOther
//   //       };
//   //     });

//   //     formik?.setFieldValue('materialTransactionsLineList', ItemsListForUpdate, false);
//   //   }
//   //   // eslint-disable-next-line react-hooks/exhaustive-deps
//   // }, [itemsList, values.inventoryId]);

//   // =========================
//   // itemsList -> lines
//   // ✅ create mode: استبعاد المكتمل من الفورم (حتى لا يكسر qty>0)
//   // ✅ مع حصر عددهم للمستخدم فقط
//   // =========================
//   useEffect(() => {
//     // ✅ لا نلمس الاستلام اليدوي، فقط المرجعي
//     if (!isReferenceReceive) return;

//     if (isUpdate === false) {
//       const itemsByInventoryId = (itemsList || [])?.filter((item: any) => item.inventoryId === values.inventoryId);
//       const itemsInOtherInventories = (itemsList || [])?.filter((item: any) => item.inventoryId !== values.inventoryId);

//       if (itemsInOtherInventories && itemsInOtherInventories.length > 0) {
//         setOtherInventoryItemsCount(itemsInOtherInventories.length);
//         setShowOtherInventoryAlert(true);
//       } else {
//         setShowOtherInventoryAlert(false);
//       }

//       // ✅ 1) احسب عدد المكتمل
//       const processed = (itemsByInventoryId || []).filter(
//         (l: any) => l?.transactionItemId && (l?.IsFullyProcessed || n(l?.RemainingQty) <= 0)
//       );
//       setProcessedRefCount(processed.length);

//       // ✅ 2) أدخل في الفورم فقط البنود التي لها متبقي > 0
//       const active = (itemsByInventoryId || []).filter(
//         (l: any) => l?.transactionItemId && !(l?.IsFullyProcessed || n(l?.RemainingQty) <= 0)
//       );

//       formik?.setFieldValue('materialTransactionsLineList', active, false);
//     } else {
//       // ✅ Update mode (كما عندك) + تحديث عدّاد المكتمل للعرض فقط
//       const EPS = 1e-9;

//       const ItemsListForUpdate = (values.materialTransactionsLineList || []).map((m: any) => {
//         const targetItem: any =
//           (itemsList || [])?.find(
//             (f: any) =>
//               String(f.transactionItemId) === String(m.transactionItemId) &&
//               String(f.transactionUom || '') === String(m.transactionUom || '')
//           ) ?? (itemsList || [])?.find((f: any) => String(f.transactionItemId) === String(m.transactionItemId));

//         const realQty = n(targetItem?.RealQty ?? m?.RealQty ?? 0);
//         const dispensedTotal = n(targetItem?.dispensedQuantity ?? 0);
//         const origDocQty = n(m.__origQty ?? m.transactionQuantityIn);

//         const dispensedOther = Math.max(0, dispensedTotal - origDocQty);
//         const remainingCap = Math.max(0, realQty - dispensedOther);
//         const fullyByOther = dispensedOther >= realQty - EPS;

//         return {
//           ...m,
//           RealQty: realQty,
//           dispensedQuantity: dispensedOther,
//           RemainingQty: remainingCap,
//           IsFullyProcessed: fullyByOther
//         };
//       });

//       // ✅ عدّاد فقط
//       setProcessedRefCount(
//         ItemsListForUpdate.filter((l: any) => l?.transactionItemId && (l?.IsFullyProcessed || n(l?.RemainingQty) <= 0))
//           .length
//       );

//       formik?.setFieldValue('materialTransactionsLineList', ItemsListForUpdate, false);
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [itemsList, values.inventoryId, isReferenceReceive, isUpdate]);

//   // =========================
//   // Add / Delete
//   // =========================
//   const handleAddLine = () => {
//     if (!canAddLine) return;

//     const newLine: MaterialTransactionsLineList = {
//       id: null,
//       transactionItemId: null,
//       transactionUom: '',
//       unitPrice: 0,
//       transactionQuantityIn: 0,
//       transactionQuantityOut: 0,
//       lotId: null,
//       inventoryId: '',
//       discount: 0,
//       codeCombinationId: '',
//       currencyCode: '',
//       currencyConversionRate: 0,
//       referenceIdMaster: '',
//       referenceIdLine: '',
//       transactionTypeId: '',
//       equivalent: 0,
//       expensesAmount: 0,
//       batchId: ''
//     };

//     const currentLines = formik.values.materialTransactionsLineList || [];
//     formik.setFieldValue('materialTransactionsLineList', [...currentLines, newLine]);
//   };

//   const handleDeleteLine = (index: number) => {
//     if (!canDeleteLine) return;
//     const updatedLines = (values.materialTransactionsLineList || []).filter((_: any, i: number) => i !== index);
//     formik.setFieldValue('materialTransactionsLineList', updatedLines);
//   };

//   // =========================
//   // Picker for line
//   // =========================
//   const openPickerForLine = async (lineIndex: number) => {
//     const lines = values.materialTransactionsLineList || [];
//     const line = lines[lineIndex];
//     if (!line) return;

//     if (!values.inventoryId) {
//       window?.alert?.(t('يجب اختيار المخزن أولاً.'));
//       return;
//     }

//     if (!canPickItem) return;

//     const params: any = { InventoryId: values.inventoryId, Scope: 1 };
//     if (values.subInventoryId) params.SubInventoryId = values.subInventoryId;

//     const picked = await pickItemUom({
//       title: i18n.language === 'en' ? 'Pick Item & UOM' : 'اختيار الصنف',
//       params,
//       bodyDefaults: { itemType: DEFAULT_ITEM_TYPE },
//       pageSize: 10,
//       searchMode: 'manual',
//       initialSearchField: 'barcodeEqual',
//       isDisabledRow: (r: any) => lineHasDuplicateItemUom(r.itemId, r.unitOfMeasure, lineIndex),
//       dynamicFields: ['qtyByLot']
//     });

//     if (!picked) return;

//     const row: any = picked.row;
//     if (!row?.itemId || !row?.unitOfMeasure) return;

//     if (lineHasDuplicateItemUom(row.itemId, row.unitOfMeasure, lineIndex)) {
//       window?.alert?.(t('لا يمكن تكرار نفس الصنف بنفس الوحدة.'));
//       return;
//     }

//     setDetailsMap(prev => mergeIntoDetailsMap(prev, [row]));
//     ensureItemUomsLoaded(String(row.itemId));

//     const updated = [...lines];
//     updated[lineIndex] = {
//       ...resetLine(updated[lineIndex]),
//       transactionItemId: row.itemId,
//       transactionUom: row.unitOfMeasure,
//       lotId: null
//     };

//     updated[lineIndex] = clampQty(updated[lineIndex]);
//     formik.setFieldValue('materialTransactionsLineList', updated);
//   };

//   // =========================
//   // Line change
//   // =========================
//   const handleLineChange = (index: number, field: string, value: any) => {
//     const updatedLines = [...(values.materialTransactionsLineList || [])];
//     if (!updatedLines[index]) return;

//     const currentLine = { ...updatedLines[index] };

//     if (field === 'clearItem') {
//       if (!canPickItem) return;
//       updatedLines[index] = resetLine(currentLine);
//       formik.setFieldValue('materialTransactionsLineList', updatedLines);
//       return;
//     }

//     if (field === 'transactionUom') {
//       if (!canChangeUom) return;

//       const itemId = currentLine.transactionItemId;
//       if (itemId && value && lineHasDuplicateItemUom(itemId, value, index)) {
//         window?.alert?.(t('لا يمكن تكرار نفس الصنف بنفس الوحدة.'));
//         return;
//       }

//       const next = { ...currentLine, transactionUom: value, lotId: null };
//       if (itemId) ensureItemUomsLoaded(String(itemId));

//       updatedLines[index] = clampQty(next);
//       formik.setFieldValue('materialTransactionsLineList', updatedLines);
//       return;
//     }

//     if (field === 'lotId') {
//       if (isPosted) return;
//       updatedLines[index] = { ...currentLine, lotId: value ?? null };
//       formik.setFieldValue('materialTransactionsLineList', updatedLines);
//       return;
//     }

//     if (field === 'transactionQuantityIn') {
//       if (!canEditQty) return;
//       if (isReferenceReceive && currentLine?.IsFullyProcessed) return;

//       const next = { ...currentLine, transactionQuantityIn: n(value) };
//       updatedLines[index] = clampQty(next);
//       formik.setFieldValue('materialTransactionsLineList', updatedLines);
//       return;
//     }

//     updatedLines[index] = { ...currentLine, [field]: value };
//     formik.setFieldValue('materialTransactionsLineList', updatedLines);
//   };

//   // =========================
//   // ✅ Lot Picker (light modal like IssueLines)
//   // =========================
//   const [lotPickerOpen, setLotPickerOpen] = useState(false);
//   const [lotPickerLineIndex, setLotPickerLineIndex] = useState<number | null>(null);
//   const [lotPickerRows, setLotPickerRows] = useState<LotRow[]>([]);
//   const [lotPickerSelectedLotId, setLotPickerSelectedLotId] = useState<any>(null);
//   const [lotPickerItemTitle, setLotPickerItemTitle] = useState<string>('');
//   const [lotSearch, setLotSearch] = useState('');

//   const openLotPicker = (lineIndex: number, rows: LotRow[], selectedLotId: any, itemTitle: string) => {
//     setLotPickerLineIndex(lineIndex);
//     setLotPickerRows(rows || []);
//     setLotPickerSelectedLotId(selectedLotId ?? null);
//     setLotPickerItemTitle(itemTitle || '');
//     setLotSearch('');
//     setLotPickerOpen(true);
//   };

//   const closeLotPicker = () => {
//     setLotPickerOpen(false);
//     setLotPickerLineIndex(null);
//     setLotPickerRows([]);
//     setLotPickerSelectedLotId(null);
//     setLotPickerItemTitle('');
//     setLotSearch('');
//   };

//   const chooseLot = (lotId: any) => {
//     if (lotPickerLineIndex == null) return;
//     handleLineChange(lotPickerLineIndex, 'lotId', lotId ?? null);
//     closeLotPicker();
//   };

//   const filteredLotRows = useMemo(() => {
//     const term = lotSearch.trim().toLowerCase();
//     if (!term) return lotPickerRows;

//     return (lotPickerRows || []).filter(r =>
//       String(r?.lotNumber ?? '')
//         .toLowerCase()
//         .includes(term)
//     );
//   }, [lotPickerRows, lotSearch]);

//   // =========================
//   // UI
//   // =========================
//   return (
//     <>
//       {/* ✅ SubInventory */}
//       <div className='row'>
//         <div className='col-lg-4 mb-1'>
//           <div className='input-group flex-nowrap'>
//             <span className='input-group-text fw-bold bg-theme bg-opacity-30'>{t('SubInventory')}</span>
//             <TreeSelect
//               disabled={isPosted}
//               loading={loading1}
//               showSearch
//               className='w-100'
//               treeLine
//               placeholder={t('Choose')}
//               allowClear
//               treeDefaultExpandAll={false}
//               value={values.subInventoryId}
//               onChange={(option: any) => formik.setFieldValue('subInventoryId', option === null ? null : option)}
//               treeData={TreeSubInventoryData?.filter((f: any) => f.parentId === values.inventoryId)}
//               treeNodeFilterProp='title'
//             />
//           </div>
//         </div>
//       </div>

//       {/* ✅ other inventory alert (كما كان) */}
//       {showOtherInventoryAlert && (
//         <div className='d-flex align-items-center justify-content-between bg-secondary bg-opacity-25 rounded p-3 mb-2'>
//           <div className='d-flex align-items-center'>
//             <span className='fs-3 text-warning fw-bold shadow-lg me-3'>{otherInventoryItemsCount}</span>
//             <div>
//               <p className='d-block m-0 fw-bold'>موجودة في مخازن أخرى</p>
//               <small className='text-dark1'>قم بتغيير المخزن للوصول إليها</small>
//             </div>
//           </div>
//           <button
//             type='button'
//             className='btn btn-sm btn-outline-secondary'
//             onClick={() => setShowOtherInventoryAlert(false)}
//           >
//             <i className='bi bi-eye-slash me-1'></i>
//             إخفاء
//           </button>
//         </div>
//       )}

//       {/* ✅ fully processed count (UI فقط) */}
//       {isReferenceReceive && processedRefCount > 0 && (
//         <div className='d-flex align-items-center justify-content-between bg-success bg-opacity-10 rounded p-3 mb-2'>
//           <div className='d-flex align-items-center'>
//             <span className='fs-3 text-success fw-bold shadow-sm me-3'>{processedRefCount}</span>
//             <div>
//               <p className='d-block m-0 fw-bold'>
//                 {i18n.language === 'en' ? 'Items fully processed' : 'أصناف تمت معالجتها بالكامل'}
//               </p>
//               <small className='text-dark1'>
//                 {i18n.language === 'en'
//                   ? 'They are excluded from the save list.'
//                   : 'تم استبعادها من بنود الحفظ لأنها بلا متبقي.'}
//               </small>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* ✅ IMPORTANT: لا نخلي detailsLoading يخفي المكون */}
//       <Loading loading={loading1} error={errorfetchItems} Type='Dots'>
//         <ItemUomPickerModal {...modalProps} />

//         {/* ✅ Header (مثل IssueLines) */}
//         <div className='card-header'>
//           <div className='d-flex flex-column flex-md-row justify-content-between gap-3'>
//             <div className='d-flex align-items-center'>
//               <i className='fas fa-truck-loading fs-5 text-theme me-2'></i>
//               <h6 className='mb-0 fw-semibold'>
//                 {t('بنود الاستلام المخزني')}
//                 <span className='badge bg-theme rounded-pill ms-2'>{allLines?.length || 0}</span>
//               </h6>

//               <span className='badge bg-inverse bg-opacity-25 text-dark ms-2'>
//                 {isManualReceive
//                   ? i18n.language === 'en'
//                     ? 'Manual'
//                     : 'يدوي'
//                   : i18n.language === 'en'
//                   ? 'Reference'
//                   : 'مرجعي'}
//               </span>

//               {isPosted && (
//                 <span className='badge bg-success ms-2'>{i18n.language === 'en' ? 'Posted' : 'مُرحّل'}</span>
//               )}
//             </div>

//             <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2'>
//               {(detailsLoading || uomsLoading) && (
//                 <div className='d-flex align-items-center'>
//                   <span className='spinner-border spinner-border-sm text-theme me-2'></span>
//                   <small className='text-muted'>
//                     {detailsLoading ? t('جاري تحميل التفاصيل...') : t('جاري تحميل الوحدات...')}
//                   </small>
//                 </div>
//               )}

//               {canAddLine && (
//                 <button
//                   type='button'
//                   className='btn btn-theme btn-sm d-flex align-items-center justify-content-center px-3 w-100 w-md-auto'
//                   onClick={handleAddLine}
//                 >
//                   <i className='fas fa-plus me-2'></i>
//                   <span>{t('AddItem')}</span>
//                 </button>
//               )}
//             </div>
//           </div>
//         </div>

//         {/* ✅ Table */}
//         <div className='card-body p-0'>
//           <div
//             className='table-responsive'
//             style={{ maxHeight: '350px', overflowY: 'auto', overflowX: 'auto', position: 'relative' }}
//           >
//             <table className='table table-hover mb-0' style={{ minWidth: '1000px' }}>
//               <thead
//                 className='shadow fw-bold'
//                 style={{
//                   position: 'sticky',
//                   top: 0,
//                   zIndex: 101,
//                   backdropFilter: 'blur(30px)'
//                 }}
//               >
//                 <tr>
//                   <th
//                     className='text-center small shadow'
//                     style={{ width: '50px', minWidth: '50px', position: 'sticky', left: 0, zIndex: 102 }}
//                   >
//                     #
//                   </th>

//                   <th className='text-center small' style={{ width: '400px', minWidth: '400px' }}>
//                     {t('ItemName')}
//                   </th>

//                   <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
//                     {t('Unit')}
//                   </th>

//                   <th className='text-center small' style={{ width: '140px', minWidth: '140px' }}>
//                     {t('LotNumber')}
//                   </th>

//                   <th className='text-center small' style={{ width: '80px', minWidth: '80px' }}>
//                     {t('Quantity')}
//                   </th>

//                   <th className='text-center small' style={{ width: '70px', minWidth: '70px' }}></th>
//                 </tr>
//               </thead>

//               <tbody className='fw-bold'>
//                 {visibleLines?.length > 0 ? (
//                   visibleLines.map((line: any, idx: number) => {
//                     const Index = idx;

//                     const infoExact = getInfoExact(line);
//                     const infoLoose = infoExact || getInfoAnyByItemId(line?.transactionItemId);

//                     const itemName = line.transactionItemId ? getItemNameForLine(line) : '';
//                     const uomName = line.transactionItemId ? getUomNameForLine(line) : '';
//                     const codeOrBarcode = line.transactionItemId ? getCodeOrBarcodeForLine(line) : '-';

//                     const isDuplicate =
//                       !readOnlyByCode &&
//                       line.transactionItemId &&
//                       line.transactionUom &&
//                       (values.materialTransactionsLineList || []).some(
//                         (l: any, i: number) =>
//                           i !== Index &&
//                           String(l?.transactionItemId || '') === String(line.transactionItemId) &&
//                           String(l?.transactionUom || '') === String(line.transactionUom)
//                       );

//                     const uomOptions = line.transactionItemId
//                       ? uomsByItemId.get(String(line.transactionItemId)) || []
//                       : [];

//                     // lots from qtyByLot + added lot for item
//                     const lots = Array.isArray(infoExact?.qtyByLot) ? infoExact.qtyByLot : [];
//                     const addedLot = line.transactionItemId ? addedLots[String(line.transactionItemId)] : null;

//                     const lotRows: LotRow[] = [
//                       { lotId: null, lotNumber: i18n.language === 'en' ? 'None' : 'بدون' },
//                       ...lots.map((x: any) => ({
//                         lotId: x?.lotId ?? x?.id,
//                         lotNumber: x?.lotNumber,
//                         expirationDate: x?.expirationDate ?? null
//                       }))
//                     ];

//                     if (addedLot && Object.keys(addedLot).length > 0) {
//                       // ensure it appears (if not already)
//                       const exists = lotRows.some(r => String(r.lotId ?? '') === String(addedLot.id ?? ''));
//                       if (!exists) {
//                         lotRows.push({
//                           lotId: addedLot.id,
//                           lotNumber: addedLot.lotNumber,
//                           expirationDate: addedLot.expirationDate
//                         });
//                       }
//                     }

//                     const selectedLot =
//                       lotRows.find(x => String(x?.lotId ?? '') === String(line.lotId ?? '')) ?? lotRows[0];

//                     const maxAllowed = getMaxAllowedQty(line);
//                     const qtyDisabled = !canEditQty || (isReferenceReceive && line?.IsFullyProcessed);
//                     const displayNo = Index + 1;

//                     return (
//                       <tr key={line?.id ?? Index}>
//                         {/* ✅ sticky # */}
//                         <td
//                           className='text-center align-middle bg-body shadow'
//                           style={{ position: 'sticky', left: 0, zIndex: 100 }}
//                         >
//                           <span className='badge bg-theme bg-opacity-80 fw-bold' style={{ fontSize: '0.85rem' }}>
//                             {displayNo}
//                           </span>
//                         </td>

//                         {/* ✅ Item cell */}
//                         <td className='align-middle'>
//                           <div style={{ width: '100%' }}>
//                             <div className={`p-2 border ${isDuplicate ? 'border-danger' : 'border-theme'} bg-body`}>
//                               <div className='d-flex align-items-start gap-2'>
//                                 <div className='flex-grow-1' style={{ minWidth: 0 }}>
//                                   {line.transactionItemId ? (
//                                     <>
//                                       <span className='fw-bold text-truncate d-block'>
//                                         {itemName ? (
//                                           itemName
//                                         ) : detailsLoading ? (
//                                           <span className='text-muted'>
//                                             <span className='spinner-border spinner-border-sm me-2'></span>
//                                             {i18n.language === 'en' ? 'Loading...' : 'جاري التحميل...'}
//                                           </span>
//                                         ) : (
//                                           `ID: ${line.transactionItemId}`
//                                         )}

//                                         {line?.IsFullyProcessed && (
//                                           <span className='badge bg-success ms-2'>
//                                             {i18n.language === 'en' ? 'Processed' : 'مكتمل'}
//                                           </span>
//                                         )}
//                                       </span>

//                                       <small className='text-muted d-block'>
//                                         {t('Unit')}: <span className='fw-bold text-theme'>{uomName || '-'}</span>
//                                         <span className='mx-2'>•</span>
//                                         {t('ItemCode')}:{' '}
//                                         <span className='fw-bold'>{String(codeOrBarcode).slice(0, 40)}</span>
//                                       </small>
//                                     </>
//                                   ) : (
//                                     <span className='text-muted'>{t('Choose')}</span>
//                                   )}

//                                   {isDuplicate && (
//                                     <small className='text-danger d-block mt-1'>
//                                       <i className='fas fa-exclamation-triangle me-1'></i>
//                                       {t('هذا الصنف مكرر مع نفس الوحدة')}
//                                     </small>
//                                   )}
//                                 </div>

//                                 {/* Actions */}
//                                 <div className='d-flex gap-1'>
//                                   <button
//                                     disabled={!canPickItem || !values.inventoryId}
//                                     type='button'
//                                     className='btn btn-sm btn-theme rounded-0'
//                                     onClick={() => openPickerForLine(Index)}
//                                     title={!values.inventoryId ? t('يجب اختيار المخزن أولاً.') : t('اختيار صنف ووحدة')}
//                                   >
//                                     <i className='fas fa-search me-1'></i>
//                                     {line.transactionItemId ? t('تغيير') : t('اختيار')}
//                                   </button>

//                                   {line.transactionItemId && (
//                                     <button
//                                       disabled={!canPickItem}
//                                       type='button'
//                                       className='btn btn-sm btn-outline-danger rounded-0'
//                                       onClick={() => handleLineChange(Index, 'clearItem', true)}
//                                       title={t('مسح الصنف')}
//                                     >
//                                       <i className='fas fa-times'></i>
//                                     </button>
//                                   )}
//                                 </div>
//                               </div>
//                             </div>
//                           </div>
//                         </td>

//                         {/* ✅ UOM */}
//                         <td className='align-middle'>
//                           <select
//                             disabled={!canChangeUom || !line.transactionItemId}
//                             className={`form-select form-select-sm bg-body ${
//                               isDuplicate ? 'border-danger' : 'border-theme'
//                             }`}
//                             value={line.transactionUom || ''}
//                             onFocus={() => {
//                               if (line.transactionItemId) ensureItemUomsLoaded(String(line.transactionItemId));
//                             }}
//                             onChange={e => handleLineChange(Index, 'transactionUom', e.target.value)}
//                             style={{ width: '100%' }}
//                           >
//                             <option value=''>{t('Choose')}</option>

//                             {line.transactionUom && uomOptions.length === 0 && (
//                               <option value={line.transactionUom}>{uomName || line.transactionUom}</option>
//                             )}

//                             {uomOptions.map((u: any) => {
//                               const uomVal = u.unitOfMeasure;
//                               const label =
//                                 (i18n.language === 'en'
//                                   ? u.unitOfMeasureName || u.unitOfMeasureName2
//                                   : u.unitOfMeasureName2 || u.unitOfMeasureName) || `ID: ${uomVal}`;

//                               return (
//                                 <option key={uomVal} value={uomVal}>
//                                   {label}
//                                 </option>
//                               );
//                             })}

//                             {uomsLoading && line.transactionItemId ? (
//                               <option value={line.transactionUom || ''} disabled>
//                                 {i18n.language === 'en' ? 'Loading...' : '...جاري التحميل'}
//                               </option>
//                             ) : null}
//                           </select>
//                         </td>

//                         {/* ✅ Lot (light picker + add lot) */}
//                         <td className='align-middle'>
//                           {(() => {
//                             const disabled = isPosted || !line.transactionItemId;
//                             const lotLabel = selectedLot?.lotNumber || (i18n.language === 'en' ? 'None' : 'بدون');

//                             return (
//                               <div className='d-flex flex-column gap-1'>
//                                 <div
//                                   className='input-group input-group-sm border border-theme'
//                                   onClick={() => {
//                                     if (disabled) return;
//                                     openLotPicker(
//                                       Index,
//                                       lotRows as any,
//                                       line.lotId,
//                                       itemName || `ID: ${line.transactionItemId}`
//                                     );
//                                   }}
//                                   style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
//                                 >
//                                   <input
//                                     readOnly
//                                     className='form-control form-control-sm bg-body'
//                                     value={lotLabel}
//                                     placeholder={t('LotNumber')}
//                                     disabled={disabled}
//                                   />
//                                   <span className='input-group-text border-0 bg-body'>
//                                     <i className='fas fa-caret-down'></i>
//                                   </span>
//                                 </div>
//                               </div>
//                             );
//                           })()}
//                         </td>

//                         {/* ✅ Qty */}
//                         <td className='align-middle'>
//                           <PopoverCustom
//                             childern={
//                               <input
//                                 type='number'
//                                 autoComplete='off'
//                                 className='form-control form-control-sm text-center px-0 border-theme bg-body'
//                                 value={line.transactionQuantityIn}
//                                 disabled={qtyDisabled}
//                                 onChange={e => handleLineChange(Index, 'transactionQuantityIn', Number(e.target.value))}
//                                 onInput={e => {
//                                   if (isPosted) return;

//                                   const input = e.target as HTMLInputElement;
//                                   input.value = input.value?.replace(/[^0-9]/g, '');

//                                   const entered = Number(input.value);
//                                   if (!Number.isFinite(maxAllowed)) return;

//                                   if (entered > maxAllowed) input.value = String(maxAllowed);
//                                 }}
//                               />
//                             }
//                             Header={
//                               isReferenceReceive
//                                 ? i18n.language === 'en'
//                                   ? `Max Allowed: ${Number.isFinite(maxAllowed) ? maxAllowed : '∞'}`
//                                   : `${t('الحد المسموح')}: ${Number.isFinite(maxAllowed) ? maxAllowed : '∞'}`
//                                 : i18n.language === 'en'
//                                 ? 'Manual receive'
//                                 : 'استلام يدوي'
//                             }
//                             Body={
//                               !line?.RemainingQty && !line?.dispensedQuantity ? (
//                                 <div className='text-muted small'>
//                                   {i18n.language === 'en' ? 'No reference quantities.' : 'لا توجد كميات مرجعية.'}
//                                 </div>
//                               ) : (
//                                 <div className='d-flex flex-column gap-1'>
//                                   <div className='d-flex justify-content-between'>
//                                     <span>{t('AddedQuantity')}</span>
//                                     <span className='fw-bold text-danger'>{n(line.dispensedQuantity)}</span>
//                                   </div>
//                                   <div className='d-flex justify-content-between'>
//                                     {line.IsFullyProcessed ? (
//                                       <span className='text-success fw-bold'>{t('FullyProcessed')}</span>
//                                     ) : (
//                                       <>
//                                         <span>{t('RemainingQuantity')}</span>
//                                         <span className='fw-bold text-success'>{n(line.RemainingQty)}</span>
//                                       </>
//                                     )}
//                                   </div>
//                                 </div>
//                               )
//                             }
//                           />
//                         </td>

//                         {/* ✅ Delete */}
//                         <td className='align-middle text-center'>
//                           {canDeleteLine ? (
//                             <button
//                               type='button'
//                               className='btn btn-sm btn-danger rounded-0'
//                               onClick={() => handleDeleteLine(Index)}
//                               title={t('Delete Line')}
//                               style={{ width: '28px', height: '28px', padding: '0' }}
//                             >
//                               <i className='fas fa-trash text-white'></i>
//                             </button>
//                           ) : (
//                             <span className='text-muted'>-</span>
//                           )}
//                         </td>
//                       </tr>
//                     );
//                   })
//                 ) : (
//                   <tr>
//                     <td colSpan={6} className='text-center py-4 text-muted'>
//                       <i className='fa fa-inbox fa-2x mb-2'></i>
//                       <br />
//                       {t('لا توجد أصناف مضافة')}
//                       <br />
//                       {canAddLine && (
//                         <button type='button' className='btn btn-theme mt-3' onClick={handleAddLine}>
//                           <i className='fas fa-plus me-2'></i>
//                           {t('إضافة اول صنف')}
//                         </button>
//                       )}
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>

//           {/* ✅ Show more (لودنج على الزر فقط) */}
//           {hasMoreLines && remainingToShow > 0 && (
//             <div className='p-2 text-center'>
//               <button type='button' className='btn btn-outline-secondary btn-sm' onClick={showMore}>
//                 {loadingMore ? (
//                   <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
//                 ) : (
//                   <i className='fas fa-chevron-down me-2'></i>
//                 )}

//                 {loadingMore
//                   ? i18n.language === 'en'
//                     ? 'Loading...'
//                     : 'جاري التحميل...'
//                   : i18n.language === 'en'
//                   ? `Show more (${remainingToShow})`
//                   : `عرض المزيد (${remainingToShow})`}
//               </button>

//               <div className='text-muted small mt-1'>
//                 {i18n.language === 'en'
//                   ? `Showing ${Math.min(visibleCount, allLines.length)} of ${allLines.length}`
//                   : `المعروض ${Math.min(visibleCount, allLines.length)} من ${allLines.length}`}
//               </div>
//             </div>
//           )}

//           {formik.errors.materialTransactionsLineList?.length ? <div className='text-danger p-2'> * مطلوب </div> : null}
//         </div>

//         {/* ✅ Card Footer */}
//         <div className='card-footer d-flex justify-content-between align-items-center'>
//           <div className='text-muted small'>
//             <i className='fas fa-list me-1'></i>
//             {t('عدد الأصناف')}: <span className='fw-bold'>{allLines?.length || 0}</span>
//           </div>

//           <div className='text-theme fw-bold'>
//             <i className='fas fa-info-circle me-2'></i>
//             {isPosted ? (i18n.language === 'en' ? 'Read only (Posted)' : 'قراءة فقط (مُرحّل)') : '-'}
//           </div>
//         </div>
//       </Loading>

//       {/* ✅ Lot Picker Modal */}
//       <NeatixModal
//         show={lotPickerOpen}
//         setShow={setLotPickerOpen}
//         size='md'
//         bodyClassName='p-0'
//         showHeader={false}
//         centered
//       >
//         <Card>
//           <CardBody>
//             <div className='d-flex justify-content-between align-items-center mb-2'>
//               <div className='fw-bold'>{i18n.language === 'en' ? 'Select Lot' : 'اختيار اللوت'}</div>
//               <button type='button' className='btn btn-sm btn-outline-secondary' onClick={closeLotPicker}>
//                 <i className='bi bi-x'></i>
//               </button>
//             </div>

//             <div className='text-muted small mb-2'>{lotPickerItemTitle}</div>

//             <input
//               className='form-control form-control-sm mb-2'
//               placeholder={i18n.language === 'en' ? 'Search lot...' : 'ابحث عن لوت...'}
//               value={lotSearch}
//               onChange={e => setLotSearch(e.target.value)}
//             />

//             <div className='d-flex gap-2 mb-2'>
//               <button type='button' className='btn btn-sm btn-outline-secondary w-100' onClick={() => chooseLot(null)}>
//                 {i18n.language === 'en' ? 'None' : 'بدون'}
//               </button>

//               <button
//                 type='button'
//                 className='btn btn-sm btn-theme w-100'
//                 onClick={() => {
//                   // open AddLotItem for current selected line
//                   const idx = lotPickerLineIndex;
//                   if (idx == null) return;
//                   const line = (values.materialTransactionsLineList || [])[idx];
//                   if (!line?.transactionItemId) return;
//                   setLineData(line);
//                   setShowAddLotModal(true);
//                 }}
//               >
//                 <i className='fas fa-plus me-1'></i>
//                 {t('NewLotNumber')}
//               </button>
//             </div>

//             <div style={{ maxHeight: 320, overflowY: 'auto' }}>
//               {filteredLotRows.length === 0 ? (
//                 <div className='text-muted small text-center py-3'>
//                   {i18n.language === 'en' ? 'No lots' : 'لا توجد لوتات'}
//                 </div>
//               ) : (
//                 <div className='list-group'>
//                   {filteredLotRows
//                     .filter(r => r?.lotId != null) // exclude "None" here, already button
//                     .map((r, i) => {
//                       const active = String(r?.lotId ?? '') === String(lotPickerSelectedLotId ?? '');
//                       return (
//                         <button
//                           key={`${r?.lotId}-${i}`}
//                           type='button'
//                           className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
//                             active ? 'active' : ''
//                           }`}
//                           onClick={() => chooseLot(r?.lotId)}
//                         >
//                           <span className='fw-bold'>{r?.lotNumber || '-'}</span>
//                           <small className={active ? 'text-white-50' : 'text-muted'}>
//                             {r?.expirationDate ? String(r.expirationDate).slice(0, 10) : ''}
//                           </small>
//                         </button>
//                       );
//                     })}
//                 </div>
//               )}
//             </div>
//           </CardBody>
//         </Card>
//       </NeatixModal>

//       {/* ✅ Add Lot Modal */}
//       <NeatixModal
//         show={showAddLotModal}
//         setShow={setShowAddLotModal}
//         size='md'
//         bodyClassName='p-0'
//         showHeader={false}
//         centered
//       >
//         <Card>
//           <CardBody>
//             {lineData && (
//               <AddLotItem lineData={lineData} onClose={() => setShowAddLotModal(false)} setAddedLots={setAddedLots} />
//             )}
//           </CardBody>
//         </Card>
//       </NeatixModal>
//     </>
//   );
// };

// export default ReceiveLines;
